import { WidgetsContainerAbstract } from "src/app/abstract/widgets/widgets-container.abstract";
import { WidgetsTypeEnum } from "src/app/enums/widgets/widgets-type.enum";
import { WidgetsAbstract } from "../../abstract/widgets/widgets.abstract";
import { FieldOptionModel } from "./field-option.model";

export class WidgetsFieldModel extends WidgetsContainerAbstract {
  readonly widgetType: WidgetsTypeEnum = WidgetsTypeEnum.FIELD;
  badge: string = 'I';
  readonly icon: string = 'crop_16_9';
  readonly placeholderClassName: string = 'column-placeholder';

  /**
   * Formly needs a unique key for each field. It's better human-friendly (id / uuid is not).
   *
   * @remarks
   *
   * - For fields this is the key that will use in rendered forms for the model ('model.key')
   * - For questions/groups this defines the namespace for the fields that live inside ('model.groupkey.key').
   *
   * @author mgsg
   */
  key: string = '';

  /**
   * Map each field to the Delivery XML corresponding field
   *
   * @deprecated Use fdKey instead
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  fd_key?: string;
  fdKey?: string;
  isoKey?: string;
  commentKey?: string;                 // Key for the comment field. If not present = field + 'Comment'
  
  type: string = 'datepicker';         // input, radio, checkbox
  name: string = 'Field';
  label: string = '';
  required: boolean = false;
  options?: FieldOptionModel[];       // Supports radio, multicheckbox, select, multiselect
  rows?: number;
  maxLength?: number;
  otherFlag?: boolean;                // Primitive has extra field "Other"
  otherKey?: string;
  otherLabel?: string;
  otherDescription?: string;
  otherMaxLength?: number;
  commentDialogOn?: string;           // Primitive has conditional comment - Dialog version
  commentFieldOn?: string;
  commentFieldOnOptional?: boolean;   // Default is comment required if present
  guidelines?: string;                // Primitive has conditional comment - Additional comment field version
  conditionalType?: string;
  relatedPrimitive?: string;
  primitiveOption?: string;
  optionsDisabledOn?: string;
  commentFieldOff?: string;

  fieldTemplate?: string;             // template is applied to group (that contains the label, field,...) 
                                      //   fieldTemplate is applied only to field
  isExtended?: boolean;               // For primitives with options, means that values are to be 
                                      //   considered as complete datapoint names vs just suffixes

  /**
   * Children here mean validations. Fields cannot have other fields as children.
   * Used the children identifier to help with tree traversal.
   */
  children: WidgetsAbstract[] = [];

  constructor() {
    super();
    this.id += this.badge;
    this.fdKey = '';
  }

  toJSON(): any {
    return {
      badge: this.badge,
      widgetType: this.widgetType,
      id: this.id,
      parentId: this.parentId ? this.parentId : undefined,
      name: this.name,

      key: this.key,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      fd_key: this.fd_key ? this.fd_key : undefined,
      fdKey: this.fdKey ? this.fdKey : undefined,
      isoKey: this.isoKey ? this.isoKey : undefined,
      type: this.type,
      required: this.required,
      label: this.label,
      description: this.description,
      rows: this.rows ? this.rows : undefined,
      maxLength: this.maxLength,
      otherFlag: this.otherFlag ? this.otherFlag : undefined,
      otherKey: this.otherKey ? this.otherKey : undefined,
      otherLabel: this.otherLabel ? this.otherLabel : undefined,
      otherMaxLength: this.otherMaxLength === undefined ? undefined : this.otherMaxLength,
      otherDescription: this.otherDescription ? this.otherDescription : undefined,  
      commentDialogOn: this.commentDialogOn ? this.commentDialogOn : undefined,
      isExtended: this.isExtended ? this.isExtended : undefined,
      commentFieldOn: this.commentFieldOn ? this.commentFieldOn : undefined,
      commentKey: this.commentKey ? this.commentKey : undefined,
      commentFieldOnOptional: this.commentFieldOnOptional ? this.commentFieldOnOptional : undefined,
      commentFieldOff: this.commentFieldOff ? this.commentFieldOff : undefined,
      optionsDisabledOn: this.optionsDisabledOn ? this.optionsDisabledOn : undefined,
      guidelines: this.guidelines ? this.guidelines : undefined,
      template: this.template ? this.template : undefined,
      fieldTemplate: this.fieldTemplate ? this.fieldTemplate : undefined,
      conditionalType: this.conditionalType ? this.conditionalType : undefined,
      relatedPrimitive: this.relatedPrimitive ? this.relatedPrimitive : undefined,
      primitiveOption: this.primitiveOption ? this.primitiveOption : undefined,
      options: this.options ? this.options : undefined,
      children: this.children
    };
  }

  /**
   * Clone from a provided origin.
   *
   * @note Id/uuid is not cloned
   * @note Without children.
   * @param origin
   * @returns
   */
  cloneFrom(origin: WidgetsFieldModel): WidgetsFieldModel {
    // WidgetsAbstract
    // this.id = origin.id; We don't want to clone the id
    if (origin.parentId) {
      this.parentId = origin.parentId;
    }
    this.name = origin.name;
    if (origin.description) {
      this.description = origin.description;
    }
    if (origin.template) {
      this.template = origin.template;
    }
    if (origin.fieldTemplate) {
      this.fieldTemplate = origin.fieldTemplate;
    }
    this.isSelected = origin.isSelected;

    // Model Specific
    if (origin.label) {
      this.label = origin.label;
    }
    this.type = origin.type;
    this.required = origin.required;
    this.key = origin.key;
    if (origin.fd_key) {
      this.fd_key = origin.fd_key;
    }
    if (origin.fdKey) {
      this.fdKey = origin.fdKey;
    }
    if (origin.isoKey) {
      this.isoKey = origin.isoKey;
    }
    if (origin.options) {
      this.options = [];
      origin.options.forEach((option) => {
        this.options?.push(option);
      });
    }
    if (origin.otherFlag) {
      this.otherFlag = origin.otherFlag;
    }
    if (origin.otherKey) {
      this.otherKey = origin.otherKey;
    }
    if (origin.otherLabel) {
      this.otherLabel = origin.otherLabel;
    }
    if (origin.otherDescription) {
      this.otherDescription = origin.otherDescription;
    }    
    if (origin.otherMaxLength !== undefined) {
      this.otherMaxLength = origin.otherMaxLength;
    }
    if (origin.commentDialogOn) {
      this.commentDialogOn = origin.commentDialogOn;
    }
    if (origin.commentFieldOn) {
      this.commentFieldOn = origin.commentFieldOn;
    }
    if (origin.commentKey) {
      this.commentKey = origin.commentKey;
    }
    if (origin.commentFieldOnOptional !== undefined && origin.commentFieldOnOptional === true) {
      this.commentFieldOnOptional = origin.commentFieldOnOptional;
    }
    if (origin.commentFieldOff) {
      this.commentFieldOff = origin.commentFieldOff;
    }
    if (origin.optionsDisabledOn) {
      this.optionsDisabledOn = origin.optionsDisabledOn;
    }
    if (origin.guidelines) {
      this.guidelines = origin.guidelines;
    }
    if (origin.rows) {
      this.rows = origin.rows;
    }
    if (origin.maxLength) {
      this.maxLength = origin.maxLength;
    }
    if (origin.conditionalType) {
      this.conditionalType = origin.conditionalType;
    }
    if (origin.relatedPrimitive) {
      this.relatedPrimitive = origin.relatedPrimitive;
    }
    if (origin.primitiveOption) {
      this.primitiveOption = origin.primitiveOption;
    }
    if (origin.isExtended) {
      this.isExtended = origin.isExtended;
    }
    return this;
  }
}
